import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SquaresFour, FilePlus, Gear, SignOut, UsersThree, UserCircle, ChartLineUp, PaintBrushBroad, Tag, ShieldStar } from "@phosphor-icons/react";

const EMBLEM = "https://static.prod-images.emergentagent.com/jobs/e7aa276a-5de1-4c83-900c-894e1c7268a2/images/df5b7e9d7d3c0712e0fada815b5b10f3496a96f18ade7bc4231629bb9c81c851.jpeg";

export default function AppLayout({ children }) {
  const { user, company, logout } = useAuth();
  const location = useLocation();

  const nav = [
    { to: "/", label: "Dashboard", icon: SquaresFour, testId: "nav-dashboard" },
    { to: "/new", label: "New Review", icon: FilePlus, testId: "nav-new-review" },
    { to: "/analytics", label: "Analytics", icon: ChartLineUp, testId: "nav-analytics" },
    { to: "/pricing", label: "Plans", icon: Tag, testId: "nav-pricing" },
    { to: "/settings", label: "Standards", icon: Gear, testId: "nav-settings" },
    ...(user?.role === "admin"
      ? [{ to: "/branding", label: "Branding", icon: PaintBrushBroad, testId: "nav-branding" },
         { to: "/users", label: "Users", icon: UsersThree, testId: "nav-users" }]
      : []),
    ...(user?.platform_owner
      ? [{ to: "/owner", label: "Owner", icon: ShieldStar, testId: "nav-owner" }]
      : []),
    { to: "/account", label: "Account", icon: UserCircle, testId: "nav-account" },
  ];

  return (
    <div className="min-h-screen grain">
      <header className="sticky top-0 z-30 bg-black border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-10">
            <Link to="/" data-testid="brand-home" className="flex items-center gap-2.5">
              <img src={EMBLEM} alt="Proposal Forensics" className="h-9 w-9 object-contain rounded-md" />
              <div className="leading-none">
                <div className="font-display font-extrabold text-[15px] tracking-tight text-white">PROPOSAL <span className="text-blue-500">FORENSICS</span></div>
                <div className="font-mono-plex text-[9px] uppercase tracking-[0.25em] text-slate-500">Forensic Review</div>
              </div>
            </Link>
            <nav className="hidden md:flex items-center gap-1">
              {nav.map((item) => {
                const active = location.pathname === item.to;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={item.testId}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                      active ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    <Icon size={16} weight={active ? "fill" : "regular"} />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right leading-tight hidden sm:block">
              <div className="text-sm font-semibold text-white" data-testid="current-user-name">{user?.name}</div>
              <div className="font-mono-plex text-[10px] uppercase tracking-widest text-slate-500">{company?.company_name || user?.role}</div>
            </div>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="h-9 w-9 flex items-center justify-center rounded-md border border-white/15 text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
            >
              <SignOut size={17} />
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-6 py-10">{children}</main>
    </div>
  );
}
